import { createFileRoute, notFound, redirect } from '@tanstack/react-router';
import {
  FUNNEL_CAMPAIGN,
  isFunnelStep,
  stepSessionRule,
} from '@/components/funnel/config/funnels';
import {
  FunnelCard,
  FunnelShell,
} from '@/components/funnel/onboarding/funnel-shell';
import { OnboardingEngine } from '@/components/funnel/onboarding/onboarding-engine';
import { Spinner } from '@/components/shared-ui/spinner';
import { getSession } from '@/services/get-session';
import * as m from '@/paraglide/messages';
import { seo } from '@/seo/seo';

export const Route = createFileRoute('/_funnel/onboarding/$step')({
  beforeLoad: ({ params }) => {
    if (!isFunnelStep(params.step)) return;
    const rule = stepSessionRule(params.step);
    if (rule === 'open') return;
    if (rule === 'anonymous') return redirectAuthenticatedToApp();
    return requireAuthenticatedSession();
  },
  loader: ({ params }) => {
    if (!isFunnelStep(params.step)) throw notFound();
    return { step: params.step };
  },
  head: () => ({
    meta: [
      ...seo({
        title: m.meta_onboarding_title(),
        description: m.meta_onboarding_description(),
      }),
    ],
  }),
  pendingComponent: OnboardingPending,
  pendingMs: 0,
  pendingMinMs: 0,
  component: OnboardingRoute,
});

async function redirectAuthenticatedToApp() {
  const check = await getSession();
  if (check._tag === 'Authenticated') throw redirect({ to: '/app/home' });
}

async function requireAuthenticatedSession() {
  const check = await getSession();
  // Only a confirmed no-session redirects. On `Unavailable` the step renders
  // anyway: ejecting a just-signed-up user to the signup step strands them —
  // their retry is a login, and logins land in the app, never back in the
  // funnel. The exposure is one step at most: moving to the next step is a
  // param change, which re-runs this guard client-side against the API.
  if (check._tag !== 'Unauthenticated') return;
  throw redirect({
    to: '/onboarding/$step',
    params: { step: 'signup' },
    replace: true,
  });
}

function OnboardingPending() {
  return (
    <FunnelShell>
      <FunnelCard className='grid min-h-80 place-items-center'>
        <Spinner className='size-7 text-azure-595' />
      </FunnelCard>
    </FunnelShell>
  );
}

function OnboardingRoute() {
  const { step } = Route.useLoaderData();
  return <OnboardingEngine campaign={FUNNEL_CAMPAIGN} step={step} />;
}
